<template>
    <card-grid-item
        :tab-widget="tabWidget"
        :card-class="['card-grid']"
        :min-w="3"
        :min-h="minHeight"
        :resized="handleResized"
        @tab-widget-settings-click="handleTabWidgetSettingsClick"
    >
        <template v-slot:card-title-prepend>
            <go-back-icon-button @click="goBack" v-if="showTable" />
        </template>
        <template v-slot:card-title>
            {{ title }}
        </template>

        <template v-slot:card-body>
            <busy-modal :show="busy" class="chart-widget-spinner" />
            <div v-if="!showTable" class="h-100">
                <vue-apex-charts
                    id="reservations-pie-chart"
                    class="reservations-pie-chart"
                    ref="reservationsPieChart"
                    type="pie"
                    height="330"
                    :series="series"
                    :options="options"
                />
            </div>
            <div v-else class="h-100">
                <reservation-card-grid
                    :getDataEndpoint="getDataEndpoint"
                    :cardGridKey="cardGridKey"
                    :limit="limit"
                    :tabWidget="tabWidget"
                    :hideEnteredColumn="hideEnteredColumn"
                />
            </div>
        </template>

        <template v-slot:tab-widget-settings-modal>
            <tab-widget-settings-modal-form
                :modal-id="tabWidgetSettingsModalID"
                :update="updateTabWidgetSettings"
                :initial-values="tabWidgetSettingsInitialValues"
                :with-airbnb-reservations="true"
                :with-hide-older-than-days="true"
                @success-update="handleSuccessTabWidgetSettingsUpdate"
                :withTableFontSize="true"
            ></tab-widget-settings-modal-form>
        </template>
    </card-grid-item>
</template>

<script>
import ActionsCellRenderer from '../common/reservations-actions-cell-renderer.vue';
import API from '../../../../api';
import BusyModal from '../../../generic/busy-modal/busy-modal.vue';
import CardGrid from '../../../generic/card-grid/card-grid.vue';
import CardGridItem from '../common/card-grid-item.vue';
import {
    DASHBOARD_PENDING_RESERVATION_COUNT_ENDPOINT,
    DASHBOARD_PENDING_RESERVATION_GRID_ENDPOINT,
    DASHBOARD_BALANCES_DUE_RESERVATION_COUNT_ENDPOINT,
    DASHBOARD_BALANCES_DUE_RESERVATION_GRID_ENDPOINT,
    DASHBOARD_REFUND_DUE_RESERVATION_COUNT_ENDPOINT,
    DASHBOARD_REFUND_DUE_RESERVATION_GRID_ENDPOINT,
} from '../../../../endpoints';
import { getWidgetGridRowsNumber } from '../helpers';
import GoBackIconButton from '../../../generic/go-back-icon-button/go-back-icon-button.vue';
import { mapGetters } from 'vuex';
import ResponsiveButton from '../../../generic/responsive-button/responsive-button.vue';
import TabWidgetSettingsModalForm from '../common/tab-widget-settings-modal-form.vue';
import VueApexCharts from 'vue3-apexcharts';
import ReservationCardGrid from '../common/reservations-card-grid.vue';
import cardGridItemBaseMixin from '../common/card-grid-item-base-mixin';

const PENDING_RESERVATION_INDEX = 0;
const BALANCES_DUE_RESERVATION_INDEX = 1;
const REFUND_DUE_RESERVATION_INDEX = 2;
const labels = ['Pending', 'Balances Due', 'Refund Due'];

const goBackButtonHeight = 43;

export default {
    name: 'ReservationsPieChartTableCard',
    mixins: [cardGridItemBaseMixin],
    components: {
        BusyModal,
        VueApexCharts,
        CardGrid,
        CardGridItem,
        ResponsiveButton,
        TabWidgetSettingsModalForm,
        GoBackIconButton,
        ReservationCardGrid,
        actionsCellRenderer: ActionsCellRenderer,
    },
    data() {
        return {
            busy: null,
            series: [],
            hasData: false,
            showTable: false,
            selectedChartPart: null,
            limit: getWidgetGridRowsNumber({
                tabWidget: this.tabWidget,
            }),
        };
    },
    mounted() {
        this.fetchChartData();
    },
    computed: {
        ...mapGetters('header', ['userPermissions']),
        getDataEndpoint() {
            switch (this.selectedChartPart) {
                case PENDING_RESERVATION_INDEX:
                    return DASHBOARD_PENDING_RESERVATION_GRID_ENDPOINT();
                case BALANCES_DUE_RESERVATION_INDEX:
                    return DASHBOARD_BALANCES_DUE_RESERVATION_GRID_ENDPOINT();
                case REFUND_DUE_RESERVATION_INDEX:
                    return DASHBOARD_REFUND_DUE_RESERVATION_GRID_ENDPOINT();
            }
        },
        hideEnteredColumn() {
            return this.selectedChartPart !== PENDING_RESERVATION_INDEX;
        },
        title() {
            return (labels[this.selectedChartPart] || '') + ' ' + (this.tabWidget.settings.name || this.defaultName);
        },
        options() {
            return {
                chart: {
                    events: {
                        dataPointSelection: (event, chartContext, config) => {
                            this.$refs.reservationsPieChart.destroy();
                            this.selectedChartPart = config.dataPointIndex;
                            this.$nextTick(() => {
                                this.showTable = true;
                                this.handleResized();
                            });
                        },
                        dataPointMouseEnter: function (event) {
                            const path = event.path || (event.composedPath && event.composedPath());
                            if (path) {
                                path[0].style.cursor = 'pointer';
                            }
                        },
                    },
                    width: '100%',
                },
                legend: {
                    show: true,
                    position: 'right',
                    fontSize: '16px',
                    itemMargin: {
                        vertical: 10,
                    },
                },
                responsive: [
                    {
                        breakpoint: 480,
                        options: {
                            chart: {
                                width: '100%',
                            },
                            legend: {
                                position: 'bottom',
                                fontSize: '12px',
                                itemMargin: {
                                    vertical: 5,
                                    horizontal: 5,
                                },
                            },
                        },
                    },
                ],
                dataLabels: {
                    enabled: true,
                    formatter: (val, { seriesIndex, w }) => {
                        return this.hasData ? this.series[seriesIndex] : 0;
                    },
                },
                tooltip: {
                    enabled: this.hasData,
                },
                labels: labels,
                colors: ['#008FFB', '#FF9800', '#775DD0'],
            };
        },
        tabWidgetSettingsInitialValues() {
            return {
                settings: {
                    name: this.tabWidget.settings.name || this.defaultName,
                    display_airbnb_reservations: this.tabWidget.settings.display_airbnb_reservations,
                    hide_older_than_days: this.tabWidget.settings.hide_older_than_days,
                    table_font_size: this.tabWidget.settings.table_font_size || this.defaultWidgetFontSize,
                },
            };
        },
    },
    methods: {
        goBack() {
            this.fetchChartData();
            this.showTable = false;
            this.selectedChartPart = null;
        },
        async fetchChartData() {
            try {
                this.busy = true;
                const params = {
                    with_airbnb: this.tabWidget.settings.display_airbnb_reservations,
                    hide_older_than_days: this.tabWidget.settings.hide_older_than_days,
                };
                const [responsePendingReservation, responseBalancesDueReservation, responseRefundDueReservation] =
                    await Promise.all([
                        API.get(DASHBOARD_PENDING_RESERVATION_COUNT_ENDPOINT(), { params }),
                        API.get(DASHBOARD_BALANCES_DUE_RESERVATION_COUNT_ENDPOINT(), { params }),
                        API.get(DASHBOARD_REFUND_DUE_RESERVATION_COUNT_ENDPOINT(), { params }),
                    ]);
                if (
                    [
                        responsePendingReservation.data,
                        responseBalancesDueReservation.data,
                        responseRefundDueReservation.data,
                    ].reduce((a, b) => a + b, 0) > 0
                ) {
                    this.series = [
                        responsePendingReservation.data,
                        responseBalancesDueReservation.data,
                        responseRefundDueReservation.data,
                    ];
                    this.hasData = true;
                } else {
                    this.series = [1, 1, 1];
                    this.hasData = false;
                }
            } catch (e) {
                this.errorToast({ text: 'Could not fetch data' });
            } finally {
                this.busy = false;
            }
        },
        handleResized() {
            this.limit = getWidgetGridRowsNumber({
                tabWidget: this.tabWidget,
            });
            this.cardGridKey += 1;
        },
    },
};
</script>

<style lang="scss">
.reservations-pie-chart {
    .apexcharts-datalabels {
        opacity: 0.9 !important;
        &.legend-mouseover-inactive {
            opacity: 0.9 !important;
        }
    }
}
</style>
